import React, { useEffect } from 'react';
import clsx from 'clsx';
import { useEditor } from '@craftjs/core';
import { TextField as InspectorTextField } from '@/components/inspector-fields/TextField/TextField';
import { useFormFieldHiddenSync } from '@/hooks/useFormFieldHiddenSync';
import IconClose from '@/assets/icons/icon-close.svg?react';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { Switch } from '@/components/inspector-fields/Switch/Switch';
import type { FormPhoneFieldProps as SharedFormPhoneFieldProps } from '@builder4/shared/components/Form';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { FORM_PHONE_FIELD_NODE_NAME, FIELD_LABEL_PHONE } from '@/utils/constants';

interface PhoneFieldInspectorProps {
  className?: string;
  /** Title shown in the card header */
  title?: string;
  onClose?: () => void;
  /** The specific field node ID to edit */
  fieldNodeId: string;
  getDefaultFieldLabel: () => string;
}

export const PhoneField: React.FC<PhoneFieldInspectorProps> = ({
  className = '',
  title = 'Phone Field',
  onClose,
  fieldNodeId,
  getDefaultFieldLabel,
}) => {
  const { actions, fieldProps } = useEditor((_, query) => {
    const node = query.node(fieldNodeId).get();
    return {
      fieldProps: (node?.data.props || {}) as SharedFormPhoneFieldProps,
    };
  });

  const { setBPProp, getBPProp } = useBreakpointAwareProp(FORM_PHONE_FIELD_NODE_NAME);

  // Read props using breakpoint-aware getter
  const label = getBPProp(fieldProps, 'label');
  const showLabel = getBPProp(fieldProps, 'showLabel');
  const placeholder = getBPProp(fieldProps, 'placeholder') ?? '';
  const required = getBPProp(fieldProps, 'required');
  const hidden = getBPProp(fieldProps, 'hidden');
  const helpText = getBPProp(fieldProps, 'helpText');
  const showHelpText = getBPProp(fieldProps, 'showHelpText');

  // Throttled — batches rapid keystrokes (text inputs)
  const setProp = (callback: (props: SharedFormPhoneFieldProps) => void) => {
    actions.history.throttle(500).setProp(fieldNodeId, callback);
  };

  // Direct — discrete actions (toggles, onBlur)
  const setPropDirect = (callback: (props: SharedFormPhoneFieldProps) => void) => {
    actions.setProp(fieldNodeId, callback);
  };

  // Sync hidden prop with layer visibility
  const { handleHiddenChange } = useFormFieldHiddenSync<SharedFormPhoneFieldProps>(
    fieldNodeId,
    hidden,
    setPropDirect,
  );

  useEffect(() => {
    actions.history.ignore().setCustom(fieldNodeId, (custom) => {
      custom.displayName = label || FIELD_LABEL_PHONE;
    });
  }, [label, actions, fieldNodeId]);

  return (
    <div
      className={clsx(
        'bg-neutral-0 flex h-[480px] max-h-[480px] w-[248px] flex-col overflow-hidden rounded-lg shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline outline-neutral-100',
        className,
      )}
      data-craft-history
    >
      {/* Card header */}
      <div className="bg-neutral-0 inline-flex h-10 min-h-10 w-full items-center justify-between rounded-t-lg border border-neutral-100 px-3">
        <div className="text-xs leading-5 font-semibold text-neutral-900">{title}</div>
        <IconSimpleButton aria-label="Close" icon={IconClose} onClick={onClose} />
      </div>

      <div className="flex min-h-0 w-full flex-1 flex-col border border-t-0 border-neutral-100">
        <div className="no-scrollbar h-[440px] flex-1 overflow-y-auto">
          <div className="flex w-full flex-col items-start justify-start gap-3 px-3 pt-3 pb-4">
            {/* Field Title */}
            <InspectorTextField
              className="w-full"
              classNameInputParent="max-w-none"
              classNameInput="max-w-[140px]"
              text="Field Label"
              inputValue={label}
              placeholderText="Phone Number"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setProp((props: SharedFormPhoneFieldProps) => {
                  setBPProp(props, 'label', e.target.value);
                })
              }
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                if (!e.target.value) {
                  setProp((props: SharedFormPhoneFieldProps) => {
                    setBPProp(props, 'label', getDefaultFieldLabel());
                  });
                }
              }}
            />

            {/* Placeholder */}
            <InspectorTextField
              className="w-full"
              classNameInputParent="max-w-none"
              classNameInput="max-w-[140px]"
              text="Placeholder"
              inputValue={placeholder}
              placeholderText="Phone"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setProp((props: SharedFormPhoneFieldProps) => {
                  setBPProp(props, 'placeholder', e.target.value);
                })
              }
            />

            {/* Label toggle */}
            <Switch
              label="Label"
              checked={showLabel}
              onCheckedChange={(checked: boolean) =>
                setPropDirect((props: SharedFormPhoneFieldProps) => {
                  setBPProp(props, 'showLabel', checked);
                })
              }
            />

            {/* Divider */}
            <div className="self-stretch pb-1">
              <div className="h-0 border-t border-neutral-100" />
            </div>

            {/* Toggles */}
            <div className="flex w-full flex-col">
              <Switch
                label="This field is required"
                checked={required}
                onCheckedChange={(checked: boolean) =>
                  setPropDirect((props: SharedFormPhoneFieldProps) => {
                    setBPProp(props, 'required', checked);
                  })
                }
              />
              <Switch
                label="This is a hidden field"
                checked={hidden}
                onCheckedChange={handleHiddenChange}
              />
              <Switch
                label="Include help text"
                checked={showHelpText}
                onCheckedChange={(checked: boolean) =>
                  setPropDirect((props: SharedFormPhoneFieldProps) => {
                    setBPProp(props, 'showHelpText', checked);
                  })
                }
              >
                {showHelpText && (
                  <InspectorTextField
                    className="w-full"
                    classNameInputParent="max-w-none"
                    classNameInput="max-w-[160px]"
                    text="Text"
                    inputValue={helpText}
                    placeholderText="234 627 2349"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setProp((props: SharedFormPhoneFieldProps) => {
                        setBPProp(props, 'helpText', e.target.value);
                      })
                    }
                  />
                )}
              </Switch>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneField;
